import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosSetup from "../../utils/axiosSetup";
import { ITaskData } from "../../models/TaskData.model";
import {
  addTaskApi,
  addTaskRemarkApi,
  fetchTasksApi,
  getPendingTasksApi,
  getTaskNotificationsApi,
  getTaskRemarkApi,
  getTodaysTask,
} from "../../api/Task.api";
import dayjs from "dayjs";

interface TaskState {
  task: ITaskData[];
  pendingTasks: ITaskData[];
  loading: boolean;
  error: string | null;
  notifications?: number;
  todayCount: number;
  pendingCount: number;
  pendingLoading: boolean;
  todayLoading: boolean;
}

const initialState: TaskState = {
  task: [],
  pendingTasks: [],
  loading: false,
  pendingLoading: false,
  todayLoading: false,
  error: null,
  todayCount: 0,
  pendingCount: 0,
};

interface UpdateTaskArgs {
  _id: string;
  action: "postpone" | "done";
  followUp?: string;
}

export const addTask = createAsyncThunk(
  "tasks/addTask",
  async (taskData: ITaskData, { rejectWithValue }) => {
    try {
      return await addTaskApi(taskData);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getTask = createAsyncThunk(
  "tasks/getTask",
  async (id: string, { rejectWithValue }) => {
    try {
      return await getTaskRemarkApi(id);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchTasks = createAsyncThunk(
  "tasks/fetchTasks",
  async (leadId: string, { rejectWithValue }) => {
    try {
      return await fetchTasksApi(leadId);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateTask = createAsyncThunk(
  "tasks/updateTask",
  async (
    args: UpdateTaskArgs,
    { rejectWithValue }: { rejectWithValue: any }
  ) => {
    try {
      const payload: UpdateTaskArgs = {
        _id: args._id,
        action: args.action,
      };
      if (args.action === "postpone" && args.followUp) {
        payload.followUp = args.followUp;
      }
      const response = await axiosSetup.put(`task/updateTask`, payload);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTodaysTasks = createAsyncThunk(
  "tasks/getTodaysTask",
  async (
    { date, assignee }: { date?: string; assignee?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await getTodaysTask(date, assignee);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const addTaskRemark = createAsyncThunk(
  "tasks/addTaskRemark",
  async (
    args: { taskId: string; remark: { text: string } },
    { rejectWithValue }
  ) => {
    try {
      return await addTaskRemarkApi(args.taskId, args.remark);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTaskRemark = createAsyncThunk(
  "tasks/getTaskRemark",
  async ({ leadId }: { leadId: string }, { rejectWithValue }) => {
    try {
      return await getTaskRemarkApi(leadId); // Return the remarks data
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch remarks");
    }
  }
);

export const getTaskNotifications = createAsyncThunk(
  "tasks/getNotifications",
  async (_, { rejectWithValue }) => {
    try {
      return await getTaskNotificationsApi();
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const getPendingTasks = createAsyncThunk(
  "task/getPendingTask",
  async (assigneeId: string | undefined | null, { rejectWithValue }) => {
    try {
      return await getPendingTasksApi(assigneeId);
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

const taskSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    handleTaskCompletion: (state, action: PayloadAction<{ _id: string }>) => {
      const taskId = action.payload._id;
      const task = state.task.find((task) => task._id === taskId);
      if (task) {
        task.done = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTask.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.task.push(action.payload.data);
      })
      .addCase(addTask.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.task = action.payload;
        state.error = null;
      })
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTask = action.payload;
        const isToday = dayjs(updatedTask.followUp).isSame(dayjs(), "day");

        // Update today's tasks
        state.task = state?.task?.filter((t) => t._id !== updatedTask._id);
        if (isToday && !updatedTask.done) {
          state.task.push(updatedTask);
        }

        // Update pending tasks
        const pendingIndex = state.pendingTasks.findIndex(
          (t) => t._id === updatedTask._id
        );
        if (pendingIndex !== -1) {
          state.pendingTasks.splice(pendingIndex, 1);
        }
        if (!isToday && !updatedTask.done) {
          state.pendingTasks.push(updatedTask);
        }
      })

      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTask.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getTask.fulfilled, (state, action) => {
        state.loading = false;
        state.task = action.payload;
        state.error = null;
      })
      .addCase(addTaskRemark.pending, (state) => {
        state.loading = true;
      })
      .addCase(addTaskRemark.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, remark } = action.payload;
        const task = state.task.find((task) => task._id === taskId);
        if (task) {
          if (!task.remarks) {
            task.remarks = [];
          }
          task.remarks.push(remark);
        }
        state.error = null;
        state.notifications = Math.max(0, state.notifications! - 1);
      })
      .addCase(addTaskRemark.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getTaskRemark.fulfilled, (state, action) => {
        state.loading = false;
        const { id, remarks } = action.payload;
        const task = state.task.find((task) => task._id === id);
        if (task) {
          task.remarks = remarks;
        }
      })
      .addCase(getTodaysTasks.pending, (state) => {
        state.todayLoading = true;
      })
      .addCase(getTodaysTasks.fulfilled, (state, action) => {
        state.todayLoading = false;
        state.task = action.payload;
      })
      .addCase(getTodaysTasks.rejected, (state, action) => {
        state.todayLoading = false;
      })
      .addCase(getTaskNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTaskNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;

        state.todayCount = payload.todayCount;
        state.pendingCount = payload.pendingCount;

        state.notifications =
          payload.total ?? state.todayCount + state.pendingCount;
      })
      .addCase(getTaskNotifications.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getPendingTasks.pending, (state) => {
        state.pendingLoading = true;
      })
      .addCase(
        getPendingTasks.fulfilled,
        (state, action: PayloadAction<ITaskData[]>) => {
          state.pendingLoading = false;
          state.pendingTasks = action.payload;
        }
      )
      .addCase(
        getPendingTasks.rejected,
        (state, action: PayloadAction<any>) => {
          state.pendingLoading = false;
          state.error = action.payload;
        }
      );
  },
});
export default taskSlice.reducer;
